import React from 'react';
import {
  Plane,
  Building2,
  Receipt,
  Building,
  Clock,
  MapPin,
  Paperclip,
} from 'lucide-react';

interface LineItemHeaderTitleProps {
  children: React.ReactNode;
  count?: number;
  icon?: React.ReactNode;
  required?: boolean;
}

const getSectionIcon = (titleStr: string) => {
  const normalized = titleStr.toLowerCase();
  if (normalized.includes('journey')) {
    return {
      icon: <Plane className="size-3.5 text-violet-600 stroke-[2.25px]" />,
      badgeBg: 'bg-violet-50/90 border-violet-200/80',
    };
  }
  if (normalized.includes('accommodation')) {
    return {
      icon: <Building2 className="size-3.5 text-sky-600 stroke-[2.25px]" />,
      badgeBg: 'bg-sky-50/90 border-sky-200/80',
    };
  }
  if (normalized.includes('employee expense')) {
    return {
      icon: <Receipt className="size-3.5 text-emerald-600 stroke-[2.25px]" />,
      badgeBg: 'bg-emerald-50/90 border-emerald-200/80',
    };
  }
  if (normalized.includes('company expense')) {
    return {
      icon: <Building className="size-3.5 text-indigo-600 stroke-[2.25px]" />,
      badgeBg: 'bg-indigo-50/90 border-indigo-200/80',
    };
  }
  if (normalized.includes('per diem')) {
    return {
      icon: <Clock className="size-3.5 text-amber-600 stroke-[2.25px]" />,
      badgeBg: 'bg-amber-50/90 border-amber-200/80',
    };
  }
  if (normalized.includes('conveyance') || normalized.includes('per km')) {
    return {
      icon: <MapPin className="size-3.5 text-teal-600 stroke-[2.25px]" />,
      badgeBg: 'bg-teal-50/90 border-teal-200/80',
    };
  }
  if (normalized.includes('attachment')) {
    return {
      icon: <Paperclip className="size-3.5 text-rose-600 stroke-[2.25px]" />,
      badgeBg: 'bg-rose-50/90 border-rose-200/80',
    };
  }
  return {
    icon: <Receipt className="size-3.5 text-indigo-600 stroke-[2.25px]" />,
    badgeBg: 'bg-indigo-50/90 border-indigo-200/80',
  };
};

const LineItemHeaderTitle: React.FC<LineItemHeaderTitleProps> = ({
  children,
  count,
  icon,
  required = false,
}) => {
  const titleStr = typeof children === 'string' ? children : '';
  const { icon: defaultIcon, badgeBg } = getSectionIcon(titleStr);
  const activeIcon = icon || defaultIcon;

  return (
    <div className="line-item-header-title flex min-w-0 items-center gap-x-2.5 text-sm font-semibold tracking-tight text-slate-800">
      <div
        className={`flex size-6.5 shrink-0 items-center justify-center rounded-md border ${badgeBg}`}
        aria-hidden="true"
      >
        {activeIcon}
      </div>
      <span className="truncate font-semibold text-slate-800">{children}</span>
      {required && (
        <span
          aria-label="Required"
          className="shrink-0 text-sm font-semibold text-rose-600"
          title="Required"
        >
          *
        </span>
      )}
      {typeof count === 'number' && count > 0 && (
        <span className="inline-flex items-center rounded-md bg-slate-100/80 px-2 py-0.5 text-[11px] font-medium text-slate-500">
          {count} {count === 1 ? 'Item' : 'Items'}
        </span>
      )}
    </div>
  );
};

export default LineItemHeaderTitle;
